/**
 * Calculation Engine for Computed Fields
 *
 * Supports:
 * - Built-in operations: sum, average, min, max, count
 * - Custom formulas with field references: {field_name} + {other_field} * 2
 * - Format options: number, currency, percent
 */

import type { FormField, FieldProperties } from '../types/form';

export interface CalculationResult {
  value: number | null;
  formatted: string;
  error?: string;
}

/**
 * Evaluate a calculation for a field
 */
export function evaluateCalculation(
  field: FormField,
  values: Record<string, unknown>,
  fields: FormField[]
): CalculationResult {
  const { properties } = field;
  const { calculationType, calculationFields = [], calculationFormula } = properties;

  try {
    let result: number | null = null;

    if (calculationType === 'formula' && calculationFormula) {
      result = evaluateFormula(calculationFormula, values, fields);
    } else if (calculationType && calculationFields.length > 0) {
      const numericValues = getNumericValues(calculationFields, values, fields);
      result = evaluateBuiltInOperation(calculationType, numericValues);
    }

    return {
      value: result,
      formatted: formatResult(result, properties),
    };
  } catch (error) {
    return {
      value: null,
      formatted: '',
      error: error instanceof Error ? error.message : 'Calculation error',
    };
  }
}

/**
 * Get numeric values from field IDs
 */
function getNumericValues(
  fieldIds: string[],
  values: Record<string, unknown>,
  fields: FormField[]
): number[] {
  const numericValues: number[] = [];

  for (const fieldId of fieldIds) {
    // Find field by ID
    const field = fields.find(f => f.id === fieldId);
    if (!field) continue;

    const value = values[field.name];
    const numValue = parseNumericValue(value);

    if (numValue !== null) {
      numericValues.push(numValue);
    }
  }

  return numericValues;
}

/**
 * Parse a value as a number
 */
function parseNumericValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    // Remove currency symbols, commas, etc.
    const cleaned = value.replace(/[$€£¥₹,\s]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }

  return null;
}

/**
 * Evaluate built-in operations
 */
function evaluateBuiltInOperation(
  operation: 'sum' | 'average' | 'min' | 'max' | 'count' | 'formula',
  values: number[]
): number | null {
  if (values.length === 0) return null;

  switch (operation) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);

    case 'average':
      return values.reduce((a, b) => a + b, 0) / values.length;

    case 'min':
      return Math.min(...values);

    case 'max':
      return Math.max(...values);

    case 'count':
      return values.length;

    default:
      return null;
  }
}

/**
 * Evaluate a custom formula
 *
 * Supports:
 * - Field references: {field_name}
 * - Basic arithmetic: + - * / ( )
 * - Math functions: abs, round, ceil, floor, sqrt, pow
 */
function evaluateFormula(
  formula: string,
  values: Record<string, unknown>,
  fields: FormField[]
): number | null {
  // Replace field references with values
  let expression = formula;

  // Match {field_name} or {field_id} patterns
  const fieldPattern = /\{([^}]+)\}/g;
  let match;

  while ((match = fieldPattern.exec(formula)) !== null) {
    const fieldRef = match[1];

    // Try to find field by name first, then by id
    let field = fields.find(f => f.name === fieldRef);
    if (!field) {
      field = fields.find(f => f.id === fieldRef);
    }

    if (field) {
      const value = values[field.name];
      const numValue = parseNumericValue(value);
      expression = expression.replace(match[0], numValue !== null ? String(numValue) : '0');
    } else {
      // Field not found, treat as 0
      expression = expression.replace(match[0], '0');
    }
  }

  // Validate expression (only allow safe characters)
  const safePattern = /^[\d\s+\-*/().]+$/;

  // Replace math function names with safe references
  expression = expression
    .replace(/\babs\(/gi, 'Math.abs(')
    .replace(/\bround\(/gi, 'Math.round(')
    .replace(/\bceil\(/gi, 'Math.ceil(')
    .replace(/\bfloor\(/gi, 'Math.floor(')
    .replace(/\bsqrt\(/gi, 'Math.sqrt(')
    .replace(/\bpow\(/gi, 'Math.pow(')
    .replace(/\bmin\(/gi, 'Math.min(')
    .replace(/\bmax\(/gi, 'Math.max(');

  // Check if expression is safe (after function replacement)
  const cleanedForValidation = expression.replace(/Math\.(abs|round|ceil|floor|sqrt|pow|min|max)/g, '');
  if (!safePattern.test(cleanedForValidation)) {
    throw new Error('Invalid formula');
  }

  try {
    // Use Function constructor for safer evaluation (no access to global scope)
    const evalFn = new Function('Math', `"use strict"; return (${expression});`);
    const result = evalFn(Math);

    if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
      return null;
    }

    return result;
  } catch {
    throw new Error('Formula evaluation failed');
  }
}

/**
 * Format the calculation result
 */
function formatResult(value: number | null, properties: FieldProperties): string {
  if (value === null) return '';

  const {
    calculationFormat = 'number',
    calculationDecimals = 2,
    calculationCurrency = '$',
    calculationPrefix = '',
    calculationSuffix = ''
  } = properties;

  let formatted: string;

  switch (calculationFormat) {
    case 'currency':
      formatted = `${calculationCurrency}${value.toFixed(calculationDecimals)}`;
      break;

    case 'percent':
      formatted = `${(value * 100).toFixed(calculationDecimals)}%`;
      break;

    case 'number':
    default:
      // Format with thousand separators
      formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: calculationDecimals,
        maximumFractionDigits: calculationDecimals,
      });
      break;
  }

  return `${calculationPrefix}${formatted}${calculationSuffix}`;
}

/**
 * Get all calculated fields from a schema
 */
export function getCalculatedFields(fields: FormField[]): FormField[] {
  return fields.filter(f => f.type === 'calculated');
}

/**
 * Check if a field depends on another field
 */
export function fieldDependsOn(field: FormField, dependencyId: string): boolean {
  const { calculationFields = [], calculationFormula } = field.properties;

  // Check explicit field list
  if (calculationFields.includes(dependencyId)) {
    return true;
  }

  // Check formula for references
  if (calculationFormula) {
    const pattern = new RegExp(`\\{${dependencyId}\\}`, 'g');
    return pattern.test(calculationFormula);
  }

  return false;
}
